import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ImagingReportFields,
  emptyImagingReportForm,
  imagingReportToForm,
} from "@/components/imaging/imaging-report-form";
import { ServiceBillingBadge } from "@/components/billing/ServiceBillingBadge";
import { ServiceQueuePanel } from "@/components/queue/ServiceQueuePanel";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  loadAppointmentPractitioners,
  listAppointmentPractitionersSync,
} from "@/core/practitioners/practitioner-store";
import type { AppointmentPractitioner } from "@/core/practitioners/types";
import { billingSnapshotForImagingExam } from "@/core/billing/service-billing-status";
import {
  listConsultationsByPatient,
} from "@/core/clinical/clinical-store";
import {
  createImagingExam,
  ImagingExamValidationError,
  listImagingExams,
  syncImagingExamsFromApi,
  updateImagingExamStatus,
} from "@/core/imaging/imaging-store";
import {
  COMMON_IMAGING_TYPES,
  IMAGING_STATUS_LABELS,
  imagingExamConsultationLabel,
  type ImagingExam,
  type ImagingExamStatus,
} from "@/core/imaging/types";
import { getPatientById, listPatients } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { routes } from "@/router/all_routes";

export function ImagingPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ImagingExamStatus | "">("");
  const [patientId, setPatientId] = useState("");
  const [prescriberId, setPrescriberId] = useState("");
  const [consultationId, setConsultationId] = useState("");
  const [examType, setExamType] = useState<string>(COMMON_IMAGING_TYPES[0]);
  const [bodyPart, setBodyPart] = useState("");
  const [clinicalIndication, setClinicalIndication] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [formAlert, setFormAlert] = useState<string | null>(null);
  const [practitioners, setPractitioners] = useState<AppointmentPractitioner[]>(() =>
    listAppointmentPractitionersSync(),
  );

  useEffect(() => {
    let cancelled = false;
    setSyncing(true);
    void syncImagingExamsFromApi().finally(() => {
      if (!cancelled) {
        setSyncing(false);
        setRefreshKey((k) => k + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const exams = useMemo(() => {
    void refreshKey;
    const all = listImagingExams();
    return statusFilter ? all.filter((e) => e.status === statusFilter) : all;
  }, [refreshKey, statusFilter]);

  const patients = useMemo(() => listPatients(), [refreshKey]);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    loadAppointmentPractitioners().then((list) => {
      setPractitioners(list);
      setPrescriberId((prev) => prev || list[0]?.id || "");
    });
  }, []);

  const patientConsultations = useMemo(() => {
    if (!patientId) return [];
    return listConsultationsByPatient(patientId);
  }, [patientId, refreshKey]);

  useEffect(() => {
    setConsultationId("");
  }, [patientId]);

  const handleCreate = (e: FormEvent) => {
    e.preventDefault();
    if (!patientId) {
      setFormError("Sélectionnez un patient.");
      return;
    }
    if (!prescriberId) {
      setFormError("Prescripteur requis.");
      return;
    }
    try {
      const result = createImagingExam({
        patientId,
        prescriberId,
        consultationId: consultationId || null,
        examType,
        bodyPart,
        clinicalIndication,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
      });
      setBodyPart("");
      setClinicalIndication("");
      setScheduledAt("");
      setFormError(null);
      setFormAlert(
        result.insurerPending
          ? (result.alertMessage ??
              "Demande créée — validation assureur en cours. L'examen peut être planifié.")
          : null,
      );
      refresh();
    } catch (err) {
      setFormError(
        err instanceof ImagingExamValidationError ? err.message : "Impossible de planifier l'examen.",
      );
    }
  };

  return (
    <div>
      <PageHeader
        title="Imagerie — examens"
        description="Prescription, planification et suivi des examens d'imagerie."
        actions={
          <Link to={routes.imagingReports} className="btn btn-outline-secondary btn-sm">
            Comptes-rendus
          </Link>
        }
      />

      <ServiceQueuePanel
        serviceId="imaging"
        defaultUnitName="Imagerie"
        enableCallNext
        title="Salle d'attente — imagerie"
      />

      <div className="ph-invoice-sheet mb-4">
        <h3 className="h6 fw-semibold mb-3">Planifier un examen</h3>
        <form className="row g-3 align-items-end ph-lab-register-form" onSubmit={handleCreate}>
          <div className="col-12 col-md-3">
            <label className="form-label small fw-semibold mb-1" htmlFor="img-patient">
              Patient
            </label>
            <select
              id="img-patient"
              className="form-select form-select-sm"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
            >
              <option value="">— Choisir —</option>
              {patients.map((p) => (
                <option key={p.id} value={p.id}>
                  {patientFullName(p)} ({p.fileNumber})
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small fw-semibold mb-1" htmlFor="img-prescriber">
              Prescripteur
            </label>
            <select
              id="img-prescriber"
              className="form-select form-select-sm"
              value={prescriberId}
              onChange={(e) => setPrescriberId(e.target.value)}
              required
            >
              <option value="">— Médecin —</option>
              {practitioners.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.displayName}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold mb-1" htmlFor="img-consultation">
              Consultation liée (optionnel)
            </label>
            <select
              id="img-consultation"
              className="form-select form-select-sm"
              value={consultationId}
              onChange={(e) => setConsultationId(e.target.value)}
              disabled={!patientId}
            >
              <option value="">— Aucune / hors consultation —</option>
              {patientConsultations.map((con) => (
                <option key={con.id} value={con.id}>
                  {new Date(con.createdAt).toLocaleDateString("fr-FR")} — {con.motif || "Sans motif"}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-sm-6 col-md-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="img-type">
              Type
            </label>
            <select
              id="img-type"
              className="form-select form-select-sm"
              value={examType}
              onChange={(e) => setExamType(e.target.value)}
            >
              {COMMON_IMAGING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-sm-6 col-md-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="img-part">
              Région
            </label>
            <input
              id="img-part"
              className="form-control form-control-sm"
              placeholder="Ex. Thorax"
              value={bodyPart}
              onChange={(e) => setBodyPart(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="img-date">
              Date / heure
            </label>
            <input
              id="img-date"
              type="datetime-local"
              className="form-control form-control-sm"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
          </div>
          <div className="col-12">
            <label className="form-label small fw-semibold mb-1" htmlFor="img-indication">
              Indication clinique
            </label>
            <input
              id="img-indication"
              className="form-control form-control-sm"
              placeholder="Motif de l'examen…"
              value={clinicalIndication}
              onChange={(e) => setClinicalIndication(e.target.value)}
            />
          </div>
          <div className="col-12">
            <button type="submit" className="btn btn-primary btn-sm">
              Planifier
            </button>
          </div>
        </form>
        {formError && <p className="small text-danger mb-0 mt-2">{formError}</p>}
        {formAlert && (
          <div className="alert alert-warning py-2 small mt-2 mb-0">{formAlert}</div>
        )}
      </div>

      <div className="ph-invoice-sheet ph-lab-filters mb-3">
        <select
          className="form-select form-select-sm w-100 w-md-auto"
          style={{ maxWidth: "14rem" }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ImagingExamStatus | "")}
        >
          <option value="">Tous les statuts</option>
          {(Object.keys(IMAGING_STATUS_LABELS) as ImagingExamStatus[]).map((s) => (
            <option key={s} value={s}>
              {IMAGING_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="ph-invoice-sheet">
        {exams.length === 0 ? (
          <p className="text-muted mb-0">Aucun examen planifié.</p>
        ) : (
          <ImagingExamTable exams={exams} statusFilter={statusFilter} onRefresh={refresh} />
        )}
      </div>
    </div>
  );
}

function ImagingExamTable({
  exams,
  statusFilter,
  onRefresh,
}: {
  exams: ImagingExam[];
  statusFilter: ImagingExamStatus | "";
  onRefresh: () => void;
}) {
  const pagination = useClientPagination(exams, [statusFilter], 25);
  const { pagedItems } = pagination;

  return (
    <div className="table-responsive ph-diag-table">
      <table className="table table-sm ph-dash-table align-middle mb-0">
        <thead>
          <tr>
            <th>N°</th>
            <th>Patient</th>
            <th>Examen</th>
            <th>Prescripteur</th>
            <th>Planifié</th>
            <th>Statut</th>
            <th>Facturation</th>
            <th className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pagedItems.map((exam) => {
            const patient = getPatientById(exam.patientId);
            const consultationLabel = imagingExamConsultationLabel(exam.consultationId);
            return (
              <tr key={exam.id} className="ph-diag-row">
                <td data-label="N°">
                  <span className="ph-inv-badge">{exam.examNumber}</span>
                </td>
                <td data-label="Patient" className="fw-medium">
                  {patient ? patientFullName(patient) : "—"}
                </td>
                <td data-label="Examen" className="small">
                  <div>
                    {exam.examType}
                    {exam.bodyPart ? ` — ${exam.bodyPart}` : ""}
                  </div>
                  {exam.clinicalIndication && (
                    <div className="text-muted">{exam.clinicalIndication}</div>
                  )}
                  {consultationLabel && (
                    <div className="text-muted">{consultationLabel}</div>
                  )}
                </td>
                <td data-label="Prescripteur" className="small">
                  {exam.prescriber || "—"}
                </td>
                <td data-label="Planifié" className="small text-muted">
                  {new Date(exam.scheduledAt).toLocaleString("fr-FR")}
                </td>
                <td data-label="Statut">
                  <span className={`hosp-diag-badge hosp-diag-badge--${exam.status}`}>
                    {IMAGING_STATUS_LABELS[exam.status]}
                  </span>
                </td>
                <td data-label="Facturation">
                  <ServiceBillingBadge
                    snapshot={billingSnapshotForImagingExam(exam.id)}
                    compact
                  />
                </td>
                <td data-label="Actions" className="text-end">
                  {exam.status === "scheduled" && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 me-2"
                      onClick={() => {
                        updateImagingExamStatus(exam.id, "in_progress");
                        onRefresh();
                      }}
                    >
                      Démarrer
                    </button>
                  )}
                  {(exam.status === "in_progress" || exam.status === "scheduled") && (
                    <Link to={routes.imagingReports} className="btn btn-link btn-sm p-0">
                      Compte-rendu
                    </Link>
                  )}
                  {exam.status === "completed" && (
                    <Link to={routes.imagingReports} className="btn btn-link btn-sm p-0">
                      Voir CR
                    </Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <TablePagination
        page={pagination.page}
        pageSize={pagination.pageSize}
        totalItems={pagination.totalItems}
        onPageChange={pagination.setPage}
        onPageSizeChange={pagination.setPageSize}
      />
    </div>
  );
}
